import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { PackagesFormValues } from '../types';
import { useMultiGuestTimeslotAvailability } from './useMultiGuestTimeslotAvailability';

interface UseExperienceSameTimesOptions {
  selectedExperiences: PackagesFormValues['selectedExperiences'];
  numberOfGuests: number;
  defaultValue?: boolean;
  dates?: (string | undefined)[];
  propertyId?: number;
  spaPackageId?: number;
  spaOpenTime?: string;
  spaCloseTime?: string;
}

interface SameTimeState {
  isSameTime: boolean;
  isToggleDisabled: boolean;
}

interface UseExperienceSameTimesReturn {
  toggleExperienceSameTime: (expIndex: number) => boolean;
  resolveSameTimeState: (
    expIndex: number,
    date: string | undefined,
    optionIds: number[]
  ) => SameTimeState;
}

/**
 * Custom hook to manage the "same time for all guests" state for experiences.
 * Handles initialization from form values and provides a toggle function that
 * updates both local state and form values while clearing guest timeslots.
 */
export const useExperienceSameTimes = ({
  selectedExperiences,
  numberOfGuests,
  defaultValue = false,
  dates = [],
  propertyId,
  spaPackageId,
  spaOpenTime,
  spaCloseTime,
}: UseExperienceSameTimesOptions): UseExperienceSameTimesReturn => {
  const { setValue } = useFormContext<PackagesFormValues>();
  const [experienceSameTimes, setExperienceSameTimes] = useState<Record<number, boolean>>({});
  const experienceSameTimesRef = useRef(experienceSameTimes);
  experienceSameTimesRef.current = experienceSameTimes;

  const { canGuestsShareTimeslotOn } = useMultiGuestTimeslotAvailability({
    dates,
    numberOfGuests,
    propertyId,
    spaPackageId,
    spaOpenTime,
    spaCloseTime,
  });

  useEffect(() => {
    if (selectedExperiences) {
      const initialState: Record<number, boolean> = {};
      selectedExperiences.forEach((exp, index) => {
        initialState[index] = exp.guestsUseSameTime ?? defaultValue;
      });
      setExperienceSameTimes(initialState);
    }
  }, [selectedExperiences?.length, defaultValue]);

  const toggleExperienceSameTime = (expIndex: number): boolean => {
    // Read from the ref so rapid consecutive toggles (before re-render) see the
    // latest committed value rather than the closure's stale state.
    const isSameTime = experienceSameTimesRef.current[expIndex] ?? false;
    const newValue = !isSameTime;

    experienceSameTimesRef.current = {
      ...experienceSameTimesRef.current,
      [expIndex]: newValue,
    };
    setExperienceSameTimes(experienceSameTimesRef.current);

    setValue(`selectedExperiences.${expIndex}.guestsUseSameTime`, newValue);

    // Clear all guest timeslots for this experience
    for (let i = 0; i < numberOfGuests; i++) {
      setValue(`guests.${i}.experiences.${expIndex}`, undefined as any);
    }

    return newValue;
  };

  const resolveSameTimeState = (
    expIndex: number,
    date: string | undefined,
    optionIds: number[]
  ): SameTimeState => {
    const canShareTimeslot = date ? canGuestsShareTimeslotOn(date, optionIds) : undefined;
    const isToggleDisabled = numberOfGuests > 1 && canShareTimeslot === false;

    return {
      isSameTime: isToggleDisabled ? false : experienceSameTimes[expIndex] ?? false,
      isToggleDisabled,
    };
  };

  return {
    toggleExperienceSameTime,
    resolveSameTimeState,
  };
};
